import { motion } from 'motion/react'
import * as React from 'react'

import { cn } from '@/utils'
import { ChartControls } from './Controls'
import { useChartBase } from './Hooks'
import type { ChartExportProps } from './types'

interface RadialChartProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ChartExportProps {
  value?: number
  max?: number
  size?: number
  strokeWidth?: number
  color?: string
  backgroundColor?: string
  label?: React.ReactNode
  sublabel?: React.ReactNode
  showPercentage?: boolean
  onChartReady?: (params: {
    containerRef: React.RefObject<HTMLDivElement>
    isFullscreen: boolean
    toggleFullscreen: () => Promise<void>
  }) => void
  showHoverControls?: boolean
}

const RadialChart = React.forwardRef<HTMLDivElement, RadialChartProps>(
  (
    {
      value = 0,
      max = 100,
      size = 200,
      strokeWidth = 20,
      className,
      color = 'hsl(var(--chart-1))',
      backgroundColor = 'hsl(var(--blue-2))',
      label,
      sublabel,
      showPercentage = true,
      onChartReady,
      projectName,
      title,
      filters,
      schema,
      dateRange,
      filename = 'chart-export.png',
      showHoverControls = false,
      ...props
    },
    ref,
  ) => {
    const {
      containerRef,
      isFullscreen,
      toggleFullscreen,
      fullscreenError,
      fullscreenSupported,
    } = useChartBase(onChartReady)

    React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement)

    const radius = (size - strokeWidth) / 2
    const circumference = 2 * Math.PI * radius
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100)
    const offset = circumference - (percentage / 100) * circumference

    return (
      <div
        ref={containerRef}
        className={cn(
          'relative flex flex-col items-center justify-center',
          isFullscreen && 'bg-white',
          className,
        )}
        style={{ width: size, height: size }}
        {...props}
      >
        <svg
          role="img"
          aria-label="Radial chart"
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="transform -rotate-90"
        >
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={backgroundColor}
            strokeWidth={strokeWidth}
          />

          {/* Progress circle */}
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
          />
        </svg>

        {/* Center text */}
        <div
          className="absolute flex flex-col items-center justify-center"
          style={{ width: size, height: size }}
        >
          {showPercentage && (
            <motion.div
              className="text-4xl font-bold"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              {Math.round(percentage)}%
            </motion.div>
          )}
          {label && (
            <div className="text-sm font-medium text-center mt-1">{label}</div>
          )}
          {sublabel && (
            <div className="text-xs text-muted-foreground text-center">
              {sublabel}
            </div>
          )}
        </div>

        {showHoverControls && (
          <ChartControls
            containerRef={containerRef}
            isFullscreen={isFullscreen}
            toggleFullscreen={toggleFullscreen}
            fullscreenError={fullscreenError}
            fullscreenSupported={fullscreenSupported}
            filename={filename}
            projectName={projectName}
            title={title}
            filters={filters}
            schema={schema}
            dateRange={dateRange}
          />
        )}
      </div>
    )
  },
)
RadialChart.displayName = 'RadialChart'

export { RadialChart }
